/**
 * Product Domain RCF/ECF Friction Bars
 * Two stacked horizontal bars: Revenue Correction (RCF) and Expense Correction (ECF).
 * RCF = Demand (55%) + MarketFit (45%); ECF = Supply (50%) + Delivery (50%).
 * Segment width = normalized contribution; color = entropy score.
 */
import React, { useRef, useEffect, useMemo } from 'react';
import * as d3 from 'd3';
import { Tooltip } from 'antd';
import styles from '../BridgeSession/EntropyFrictionBars.module.css';

const RCF_FACTORS = ['demand', 'marketfit'] as const;
const ECF_FACTORS = ['supply', 'delivery'] as const;

const SUBDOMAIN_LABELS: Record<string, string> = {
  demand: 'Demand',
  marketfit: 'MarketFit',
  supply: 'Supply',
  delivery: 'Delivery',
};

export interface ProductSubdomainData {
  entropy: number;
  correction: number;
  label: string;
}

export interface ProductRCFECFBarsProps {
  /** Subdomain key (lowercase) -> { entropy, correction, label } */
  scopeMap: Map<string, ProductSubdomainData>;
  barHeight?: number;
  /** Total RCF to display */
  rcfCorrection?: number;
  /** Total ECF to display */
  ecfCorrection?: number;
}

const getEntropyColor = (score: number): string => {
  if (score >= 7) return '#cf1322';
  if (score >= 5) return '#fa8c16';
  if (score >= 4) return '#fadb14';
  if (score >= 3) return '#95de64';
  return '#389e0d';
};

interface Segment {
  factorId: string;
  label: string;
  entropy: number;
  correction: number;
  friction: number;
  contributionPct: number;
  width: number;
  color: string;
}

function buildSegments(
  factorIds: readonly string[],
  scopeMap: Map<string, ProductSubdomainData>
): Segment[] {
  const segments: Segment[] = [];
  let totalFriction = 0;

  for (const factorId of factorIds) {
    const data = scopeMap.get(factorId);
    if (!data) continue;

    const correction = Math.max(0, Math.min(1, data.correction ?? 0));
    const friction = 1 - correction;
    totalFriction += friction;

    segments.push({
      factorId,
      label: data.label || SUBDOMAIN_LABELS[factorId] || factorId,
      entropy: data.entropy,
      correction,
      friction,
      contributionPct: 0,
      width: 0,
      color: getEntropyColor(data.entropy),
    });
  }

  const divisor = totalFriction > 0 ? totalFriction : segments.length;
  segments.forEach((s) => {
    s.contributionPct = divisor > 0 ? ((s.friction > 0 ? s.friction : 1) / divisor) * 100 : 0;
    s.width = s.contributionPct / 100;
  });

  return segments;
}

export const ProductRCFECFBars: React.FC<ProductRCFECFBarsProps> = ({
  scopeMap,
  barHeight = 24,
  rcfCorrection,
  ecfCorrection,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const { revenueSegments, expenseSegments } = useMemo(() => {
    const revenue = buildSegments(RCF_FACTORS, scopeMap);
    const expense = buildSegments(ECF_FACTORS, scopeMap);
    return { revenueSegments: revenue, expenseSegments: expense };
  }, [scopeMap]);

  const hasData = revenueSegments.length > 0 || expenseSegments.length > 0;

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || !hasData) return;

    const container = containerRef.current;
    const svgEl = svgRef.current;

    const animationFrameId = requestAnimationFrame(() => {
      if (!svgEl || !containerRef.current) return;
      const svg = d3.select(svgEl);
      svg.selectAll('*').remove();

      const labelWidth = rcfCorrection != null || ecfCorrection != null ? 220 : 140;
      const margin = { top: 8, right: 8, bottom: 8, left: labelWidth };
      const width = Math.max(container.clientWidth, 200);
      const innerWidth = width - margin.left - margin.right;
      const barGap = 16;
      const revenueBarY = 0;
      const expenseBarY = revenueBarY + barHeight + barGap;

      const g = svg
        .attr('width', width)
        .attr('height', expenseBarY + barHeight + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      const drawBar = (segments: Segment[], barY: number) => {
        let offset = 0;
        segments.forEach((seg) => {
          if (seg.width <= 0) return;
          const segWidth = seg.width * innerWidth;
          const rect = g
            .append('g')
            .attr('data-factor-id', seg.factorId);
          rect
            .append('rect')
            .attr('x', offset)
            .attr('y', barY)
            .attr('width', Math.max(2, segWidth))
            .attr('height', barHeight)
            .attr('fill', seg.color)
            .attr('stroke', '#000')
            .attr('stroke-width', 1)
            .attr('rx', 2);
          rect
            .append('title')
            .text(
              `${seg.label}: entropy ${seg.entropy}/10, correction ${seg.correction.toFixed(2)}, contribution ${seg.contributionPct.toFixed(1)}%`
            );
          offset += segWidth;
        });
      };

      const revenueLabel =
        rcfCorrection != null
          ? `Revenue Friction (RCF: ${rcfCorrection.toFixed(2)})`
          : 'Revenue Friction (RCF)';
      g.append('text')
        .attr('x', -8)
        .attr('y', revenueBarY + barHeight / 2)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .style('font-size', '12px')
        .style('fill', '#595959')
        .text(revenueLabel);

      drawBar(revenueSegments, revenueBarY);

      const expenseLabel =
        ecfCorrection != null
          ? `Expense Friction (ECF: ${ecfCorrection.toFixed(2)})`
          : 'Expense Friction (ECF)';
      g.append('text')
        .attr('x', -8)
        .attr('y', expenseBarY + barHeight / 2)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .style('font-size', '12px')
        .style('fill', '#595959')
        .text(expenseLabel);

      drawBar(expenseSegments, expenseBarY);
    });

    return () => cancelAnimationFrame(animationFrameId);
  }, [revenueSegments, expenseSegments, barHeight, hasData, rcfCorrection, ecfCorrection]);

  if (!hasData) return null;

  return (
    <div className={styles.barsWrap}>
      <div className={styles.barsLabel}>RCF/ECF contributions by subdomain</div>
      <div ref={containerRef} className={styles.barsContainer}>
        <svg ref={svgRef} />
      </div>
      <div className={styles.legends}>
        {[...revenueSegments, ...expenseSegments].map((seg) => (
          <Tooltip
            key={seg.factorId}
            title={
              <div>
                <div>
                  <strong>{seg.label}</strong>
                </div>
                <div>Entropy: {seg.entropy}/10</div>
                <div>Correction: {seg.correction.toFixed(2)}</div>
                <div>Contribution: {seg.contributionPct.toFixed(1)}%</div>
              </div>
            }
          >
            <span className={styles.legendItem}>
              <span
                className={styles.legendSwatch}
                style={{ backgroundColor: seg.color }}
              />
              {seg.label}
            </span>
          </Tooltip>
        ))}
      </div>
    </div>
  );
};

export default ProductRCFECFBars;
